<template>
  <main class="container py-5">

    <div class="mb-4">

      <span
        class="text-primary
               fw-semibold"
      >
        CHECKOUT
      </span>

      <h2 class="fw-bold">
        Complete Your Order
      </h2>

      <p class="text-muted">
        Enter your delivery information
        to place your order.
      </p>

    </div>

    <!-- Empty cart -->
    <div
      v-if="cart.length === 0"
      class="alert alert-info"
    >
      Your cart is empty.

      <router-link to="/books">
        Browse Books
      </router-link>
    </div>

    <div
      v-else
      class="row g-4"
    >

      <!-- Customer Info -->
      <div class="col-lg-7">

        <div class="checkout-card">

          <h4 class="fw-bold mb-4">
            Delivery Information
          </h4>

          <form
            @submit.prevent="
              placeOrder
            "
          >

            <div class="row">

              <div class="col-md-6 mb-3">

                <label
                  class="form-label"
                >
                  Full Name
                </label>

                <input
                  v-model.trim="
                    form.name
                  "
                  type="text"
                  class="form-control"
                  placeholder="Your full name"
                  required
                >

              </div>

              <div class="col-md-6 mb-3">

                <label
                  class="form-label"
                >
                  Phone
                </label>

                <input
                  v-model.trim="
                    form.phone
                  "
                  type="tel"
                  class="form-control"
                  placeholder="Your phone number"
                  required
                >

              </div>

            </div>

            <div class="mb-3">

              <label
                class="form-label"
              >
                Email
              </label>

              <input
                v-model.trim="
                  form.email
                "
                type="email"
                class="form-control"
                placeholder="Your email"
                required
              >

            </div>

            <div class="mb-3">

              <label
                class="form-label"
              >
                Address
              </label>

              <textarea
                v-model.trim="
                  form.address
                "
                class="form-control"
                rows="3"
                placeholder="Delivery address"
                required
              ></textarea>

            </div>

            <div class="mb-4">

              <label
                class="form-label"
              >
                Payment Method
              </label>

              <div
                class="payment-options"
              >

                <label
                  class="payment-option"
                  :class="{
                    selected:
                      form.payment ===
                      'cash'
                  }"
                >

                  <input
                    v-model="
                      form.payment
                    "
                    type="radio"
                    value="cash"
                  >

                  <span>
                    💵
                  </span>

                  <div>
                    <strong>
                      Cash on Delivery
                    </strong>

                    <small>
                      Pay when your
                      order arrives
                    </small>
                  </div>

                </label>

                <label
                  class="payment-option"
                  :class="{
                    selected:
                      form.payment ===
                      'card'
                  }"
                >

                  <input
                    v-model="
                      form.payment
                    "
                    type="radio"
                    value="card"
                  >

                  <span>
                    💳
                  </span>

                  <div>
                    <strong>
                      Card
                    </strong>

                    <small>
                      Demo payment
                    </small>
                  </div>

                </label>

              </div>

            </div>

            <div
              v-if="form.payment === 'card'"
              class="alert alert-warning"
            >
              This is a demo checkout.
              Do not enter real card
              information.
            </div>

            <button
              type="submit"
              class="btn
                     btn-primary
                     btn-lg
                     w-100"
              :disabled="processing"
            >
              {{
                processing
                  ? 'Processing...'
                  : 'Place Order'
              }}
            </button>

          </form>

        </div>

      </div>

      <!-- Order Summary -->
      <div class="col-lg-5">

        <div class="checkout-card">

          <h4 class="fw-bold mb-4">
            Order Summary
          </h4>

          <div
            v-for="item in cart"
            :key="item.ISBN"
            class="summary-product"
          >

            <img
              :src="item.image"
              :alt="item.name"
            >

            <div class="flex-grow-1">

              <h6 class="mb-1">
                {{ item.name }}
              </h6>

              <small
                class="text-muted"
              >
                Qty:
                {{ item.quantity }}
              </small>

            </div>

            <strong>
              {{
                formatPrice(
                  item.price *
                  item.quantity
                )
              }}
            </strong>

          </div>

          <hr>

          <div class="summary-row">

            <span>
              Subtotal
            </span>

            <strong>
              {{ formatPrice(subtotal) }}
            </strong>

          </div>

          <div class="summary-row">

            <span>
              Shipping
            </span>

            <strong>
              {{
                shipping === 0
                  ? 'FREE'
                  : formatPrice(
                      shipping
                    )
              }}
            </strong>

          </div>

          <hr>

          <div
            class="summary-row
                   total-row"
          >

            <span>
              Total
            </span>

            <strong>
              {{ formatPrice(total) }}
            </strong>

          </div>

        </div>

      </div>

    </div>

    <!-- Success -->
    <div
      v-if="orderSuccess"
      class="success-overlay"
    >

      <div class="success-card">

        <div class="success-icon">
          ✓
        </div>

        <h2>
          Order Confirmed!
        </h2>

        <p class="text-muted">
          Thank you,
          {{ form.name }}.
          Your order has been placed
          successfully.
        </p>

        <p>
          Order #
          <strong>
            {{ orderNumber }}
          </strong>
        </p>

        <button
          class="btn btn-primary"
          @click="
            $router.push('/buyer')
          "
        >
          Continue Shopping
        </button>

      </div>

    </div>

  </main>
</template>

<script>
export default {
  name: 'Checkout',

  data() {
    return {
      processing: false,

      orderSuccess: false,

      orderNumber: '',

      form: {
        name: '',
        phone: '',
        email: '',
        address: '',
        payment: 'cash'
      }
    }
  },

  computed: {
    cart() {
      return this.$store.state.cart
    },

    subtotal() {
      return this.$store.getters
        .cartSubtotal
    },

    shipping() {
      return this.$store.getters
        .shippingCost
    },

    total() {
      return this.$store.getters
        .cartTotal
    }
  },

  mounted() {
    const user =
      this.$store.getters
        .currentUser

    if (user) {
      this.form.name =
        user.name || ''

      this.form.email =
        user.email || ''
    }
  },

  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat(
        'en-SA',
        {
          style: 'currency',
          currency: 'SAR'
        }
      ).format(value)
    },

    placeOrder() {
      if (
        this.cart.length === 0
      ) {
        return
      }

      this.processing = true

      setTimeout(() => {

        this.orderNumber =
          'BK-' +
          Date.now()
            .toString()
            .slice(-6)

        this.$store.dispatch(
          'clearCart'
        )

        this.processing = false

        this.orderSuccess =
          true

      }, 1200)
    }
  }
}
</script>

<style scoped>
.checkout-card {
  background: white;

  border-radius: 18px;

  padding: 30px;

  box-shadow:
    0 5px 25px
    rgba(0, 0, 0, 0.06);
}

.payment-options {
  display: grid;

  grid-template-columns:
    1fr 1fr;

  gap: 15px;
}

.payment-option {
  display: flex;

  align-items: center;

  gap: 12px;

  padding: 15px;

  border: 2px solid #dee2e6;

  border-radius: 12px;

  cursor: pointer;

  transition: 0.2s;
}

.payment-option:hover {
  border-color: #86b7fe;
}

.payment-option.selected {
  border-color: #0d6efd;

  background: #eef5ff;
}

.payment-option input {
  accent-color: #0d6efd;
}

.payment-option > span {
  font-size: 1.5rem;
}

.payment-option div {
  display: flex;

  flex-direction: column;

  gap: 2px;
}

.payment-option small {
  color: #6c757d;
}

.summary-product {
  display: flex;

  align-items: center;

  gap: 12px;

  margin-bottom: 18px;
}

.summary-product img {
  width: 60px;

  height: 70px;

  border-radius: 8px;

  object-fit: cover;
}

.summary-row {
  display: flex;

  justify-content:
    space-between;

  margin-bottom: 15px;
}

.total-row {
  font-size: 1.25rem;
}

.success-overlay {
  position: fixed;

  inset: 0;

  background:
    rgba(0, 0, 0, 0.65);

  display: flex;

  justify-content: center;

  align-items: center;

  z-index: 9999;

  padding: 20px;
}

.success-card {
  max-width: 450px;

  width: 100%;

  background: white;

  border-radius: 22px;

  padding: 40px;

  text-align: center;

  box-shadow:
    0 20px 60px
    rgba(0, 0, 0, 0.25);
}

.success-icon {
  width: 75px;
  height: 75px;

  margin:
    0 auto 20px;

  display: flex;

  justify-content: center;
  align-items: center;

  border-radius: 50%;

  background: #d1e7dd;

  color: #198754;

  font-size: 2rem;

  font-weight: 700;
}

@media (max-width: 576px) {
  .payment-options {
    grid-template-columns: 1fr;
  }

  .checkout-card {
    padding: 22px;
  }
}
</style>